import { Navigate, Outlet, useLocation } from "react-router-dom";
import bgImg from "../../../assets/img/background/login.jpg";
import logo from "../../../assets/img/logo/transparent_logo.png";
import { useSession } from "@user-management-context/primary/hooks/use-session";
import { resolveLandingUrl } from "@user-management-context/shared/domain/landing-url";
import type { Nullable } from "@shared-kernel/core/types/nullable";
import publicSite from "@config/public-site";

interface LocationState {
  from?: string;
}

// Legal links live on the public marketing site (lesateliersoo.fr), which owns
// the canonical privacy policy and legal notice for the whole company.
const PRIVACY_POLICY_URL = `${publicSite.url}/politique-de-confidentialite`;
const LEGAL_NOTICE_URL = `${publicSite.url}/mentions-legales`;

const LoginOutlet = () => {
  const { user, activeRole } = useSession();
  const { state } = useLocation();
  // Auth guard: on successful login, the role-based router in App.tsx
  // rebuilds and the new instance mounts at the unchanged `/login` URL,
  // briefly rendering <LoginPage/> before its navigate effect fires.
  // Redirecting here intercepts that render and lands authenticated users
  // straight on their dashboard. Preserves `state.from` (set by
  // ProtectedRoute) so deep-linked URLs survive the auth round-trip.
  if (user) {
    const redirect = (state as Nullable<LocationState>)?.from ?? null;
    const landing = resolveLandingUrl({ activeRole, isAdmin: user.isAdmin, redirect }) ?? "/";
    return <Navigate replace to={landing} />;
  }
  return (
    <div className="container relative grid h-screen w-screen flex-col items-center justify-center bg-slate-900 sm:max-w-none lg:grid-cols-6 lg:px-0">
      <div className="relative col-span-4 hidden h-screen flex-col bg-muted dark:border-r lg:flex">
        <img className="size-full object-cover" src={bgImg} alt="Authentication" />
      </div>
      <div className="flex flex-col items-center space-y-6 text-white lg:col-span-2">
        <div className="flex w-[250px] justify-center">
          <img src={logo} alt="logo" width="250" />
        </div>
        <div className="px-4 lg:w-[350px]">
          <Outlet />
        </div>
        <footer className="px-4 text-center text-xs text-white/60">
          <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white">
            Politique de confidentialité
          </a>
          <span className="mx-2">·</span>
          <a href={LEGAL_NOTICE_URL} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white">
            Mentions légales
          </a>
        </footer>
      </div>
    </div>
  );
};

export default LoginOutlet;
